/**
 * Host Panel icons, all `aria-hidden`, since the meaning always sits in the text beside them.
 *
 * They live here rather than in `ui/icon` because none exists in the Design System's glyph set
 * (docs/internal/ds-icon-gaps.md); when the DS ships these drawings, this file becomes imports.
 */

type IconProps = {
  className?: string;
};

/** KPI icon: decorative stroke weight, inheriting the circle's colour. */
function KpiIcon({ children, className }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

/** Action icon: heavier stroke than the decorative ones. */
function ActionIcon({
  children,
  size = 14,
  className,
}: IconProps & { children: React.ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className ?? "flex-none"}
    >
      {children}
    </svg>
  );
}

export function WalletIcon(props: IconProps) {
  return (
    <KpiIcon {...props}>
      <path d="M3 7h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M3 7V6a2 2 0 0 1 2-2h11" />
      <path d="M17 13h.01" />
    </KpiIcon>
  );
}

export function GiftIcon(props: IconProps) {
  return (
    <KpiIcon {...props}>
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13M4 12v9h16v-9M12 8S10 3 7.5 3 5 6 5 6s2 2 7 2zM12 8s2-5 4.5-5S19 6 19 6s-2 2-7 2z" />
    </KpiIcon>
  );
}

export function VisitorsIcon(props: IconProps) {
  return (
    <KpiIcon {...props}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </KpiIcon>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <KpiIcon {...props}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </KpiIcon>
  );
}

export function ArrowRightIcon({ size = 14, ...props }: IconProps & { size?: number }) {
  return (
    <ActionIcon size={size} {...props}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </ActionIcon>
  );
}

export function WhatsappIcon(props: IconProps) {
  return (
    <ActionIcon {...props}>
      <path d="M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 20l1.2-5.2A8.5 8.5 0 1 1 21 11.5z" />
    </ActionIcon>
  );
}

export function EditListIcon(props: IconProps) {
  return (
    <ActionIcon {...props}>
      <path d="M4 6h11M4 12h7M4 18h7" />
      <path d="M14 20l6-6 1.5 1.5-6 6H14z" />
    </ActionIcon>
  );
}

export function CopyIcon(props: IconProps) {
  return (
    <ActionIcon {...props}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </ActionIcon>
  );
}


